/**
 * What every traffic reading has in common.
 *
 * All six answer the same question from two different sources, and the source
 * is never an implementation detail a reader can ignore: it decides which
 * figures exist, how a session was counted, and whether a number is a floor or
 * a measurement. So it is stated on the screen, not in a settings page
 * somebody would have to remember to check.
 */

import { Button, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import type { ReactNode } from "react";
import { Link } from "react-router";
import type { TrafficFrame, TrafficSlice } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { brand, settings } from "~/core/paths";
import { DrillLink } from "~/modules/analytics/components/detail";
import { ENGINES } from "~/modules/analytics/lib/engines";
import { readingPath } from "~/modules/analytics/lib/loop";

/**
 * Where the site-setup screens live: a tab of Settings › Site setup, because
 * each is something placed on or checked against the brand's own site rather
 * than a reading of what the engines said.
 */
export function siteSetup(entityId: string, tab?: "check" | "goals" | "speed" | "badge" | "crawlers"): string {
	return tab ? `${settings(entityId, "site")}?tab=${tab}` : settings(entityId, "site");
}

/** The AI referrals reading, open on one of its tabs. */
export function referrals(entityId: string, tab?: "paths" | "attribution"): string {
	const path = readingPath(brand(entityId), "referrals");
	return tab ? `${path}?tab=${tab}` : path;
}

/**
 * The line under the lede saying what measured these figures.
 *
 * Also the only place a reader learns that the range they asked for was
 * measured by two different things. The older stretch is named rather than
 * summed in: GA counts a session over a cookie identity with its own timezone
 * and its own withheld rows, and adding that to ours would be one number that
 * is true of neither.
 */
export function SourceNote({ frame }: { frame: TrafficFrame }) {
	const t = useT();
	const { locale } = useI18n();
	if (!frame.source || frame.setup_required) return null;

	const earlier = frame.earlier[0];
	return (
		<p className="hint mb-(--s-8)">
			{t(`traffic.source.${frame.source}`)}
			{frame.source === "ga4" ? <> · {t("traffic.source.ga4Floor")}</> : null}
			{earlier ? (
				<>
					{" · "}
					{t("traffic.source.switched", {
						day: fmt.shortDate(locale, earlier.to),
						source: t(`traffic.source.${earlier.source}`),
					})}
				</>
			) : null}
			{frame.segment ? (
				<>
					{" · "}
					{t("traffic.source.window", {
						from: fmt.shortDate(locale, frame.segment.from),
						to: fmt.shortDate(locale, frame.segment.to),
					})}
				</>
			) : null}
		</p>
	);
}

/**
 * What a reading draws when its source cannot answer it.
 *
 * An explanation rather than an empty chart. A chart with no bars says the
 * traffic was zero; the truth here is that nobody counted, and those are
 * different enough that one of them would send somebody to fix a page that is
 * working.
 */
export function Unavailable({ frame, entityId }: { frame: TrafficFrame; entityId: string }) {
	const t = useT();
	if (!frame.source || (frame.source === "site" && frame.setup_required)) {
		return (
			<section className="stack" aria-label={t("traffic.start.title")}>
				<h2>{t("traffic.start.title")}</h2>
				<p className="muted">{t("traffic.start.body")}</p>
				<ol className="stack list-decimal pl-(--s-8)">
					<li>
						<strong>{t("traffic.start.install")}</strong>
						<p className="hint">{t("traffic.start.installBody")}</p>
					</li>
					<li>
						<strong>{t("traffic.start.verify")}</strong>
						<p className="hint">{t("traffic.start.verifyBody")}</p>
					</li>
					<li>
						<strong>{t("traffic.start.read")}</strong>
						<p className="hint">{t("traffic.start.readBody")}</p>
					</li>
				</ol>
				<div className="row-actions">
					<Button asChild variant="primary">
						<Link to={siteSetup(entityId)}>{t("traffic.noSourceAction")}</Link>
					</Button>
					<DrillLink to={siteSetup(entityId, "check")}>{t("traffic.start.verifyAction")}</DrillLink>
				</div>
			</section>
		);
	}
	return (
		<section className="stack">
			<h2>{t(frame.authorization_required ? "traffic.reconnectTitle" : "traffic.siteOnlyTitle")}</h2>
			<p className="muted">{t(frame.authorization_required ? "traffic.gaReauthorize" : "traffic.needsSite")}</p>
			<div className="row-actions">
				<Button asChild variant="primary">
					<Link to={siteSetup(entityId)}>{t("traffic.noSourceAction")}</Link>
				</Button>
				<DrillLink to={referrals(entityId)}>{t("reading.referrals")}</DrillLink>
			</div>
		</section>
	);
}

export function TrafficEmpty({
	title,
	body,
	entityId,
	destination = "live",
	action,
}: {
	title: string;
	body: string;
	entityId: string;
	destination?: "live" | "traffic" | "goals";
	action?: string;
}) {
	const t = useT();
	const to = {
		live: [siteSetup(entityId, "check"), "site.tab.check"],
		traffic: [referrals(entityId), "reading.referrals"],
		goals: [siteSetup(entityId, "goals"), "site.tab.goals"],
	}[destination];
	return (
		<EmptyState
			size="compact"
			title={t(title)}
			description={t(body)}
			action={<DrillLink to={to[0]}>{t(action ?? to[1])}</DrillLink>}
		/>
	);
}

/** A dimension's rows, the same shape on every reading that draws one. */
export function Slices({
	rows,
	label,
	render,
}: {
	rows: TrafficSlice[];
	label: string;
	/** How the key reads — an engine has a name, a path is a path. */
	render?: (key: string) => ReactNode;
}) {
	const t = useT();
	const { locale } = useI18n();
	if (rows.length === 0) return <EmptyState size="compact" title={t("traffic.empty")} />;

	return (
		<div className="table-scroll">
			<Table variant="resource">
				<TableHeader>
					<TableRow>
						<TableHead className="wrap">{label}</TableHead>
						<TableHead className="num">{t("traffic.sessions")}</TableHead>
						<TableHead className="num">{t("traffic.users")}</TableHead>
						<TableHead className="num">{t("traffic.engagementRate")}</TableHead>
						<TableHead className="num">{t("traffic.conversions")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.key}>
							<TableCell className="wrap">{render ? render(row.key) : row.key}</TableCell>
							<TableCell className="num">{fmt.number(locale, row.sessions)}</TableCell>
							<TableCell className="num">{count(locale, row.active_users)}</TableCell>
							<TableCell className="num">{rate(locale, row.engagement_rate)}</TableCell>
							<TableCell className="num">{fmt.number(locale, row.conversions)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

/**
 * An engine's name.
 *
 * Two tiers, because the server has two: an engine this product samples
 * carries the same `engine.<key>` string the filter bar and every stage-one
 * screen already use, and an assistant it does not sample gets its own string
 * under `traffic.engine.*`. Keeping the first tier on the shared key is what
 * stops the same assistant reading as two different products on two screens.
 */
export function engineLabel(t: (key: string) => string, key: string): string {
	if (!key) return t("traffic.direct");
	return ENGINES.some((engine) => engine.key === key) ? t(`engine.${key}`) : t(`traffic.engine.${key}`);
}

/** GA reports device categories we do not have a word for; those read as sent. */
export function deviceLabel(t: (key: string) => string, key: string): string {
	return ["desktop", "mobile", "tablet"].includes(key) ? t(`traffic.device.${key}`) : key;
}

/**
 * A count, or an em dash where the source cannot give one.
 *
 * Null is not zero: on a GA-measured range there is no honest visitor count to
 * show, and a zero would say nobody came.
 */
export function count(locale: Parameters<typeof fmt.number>[0], value: number | null): string {
	return value === null ? "—" : fmt.number(locale, value);
}

/**
 * A rate, or an em dash.
 *
 * Null is not zero anywhere in this product: a zero engagement rate says
 * nobody engaged, and what null says is that nobody came.
 *
 * The server returns a ratio and `fmt.percent` takes 0-100, so the scaling
 * happens here once rather than at each of the six call sites -- which is how
 * an engagement rate of a half first rendered as "0.5%".
 */
export function rate(locale: Parameters<typeof fmt.percent>[0], value: number | null): string {
	return value === null ? "—" : fmt.percent(locale, value * 100, 1);
}

/**
 * An engagement time, or an em dash.
 *
 * The server counts seconds and `fmt.duration` reads milliseconds; without the
 * conversion a 45-second visit printed as "45ms".
 */
export function seconds(value: number | null): string {
	if (value === null) return "—";
	const whole = Math.round(value);
	// `fmt.duration` reads milliseconds and prints sub-second values in them,
	// so a visit too short to round up to a second came out as "0ms" — which
	// reads as a precision error rather than as "they left immediately".
	return whole === 0 ? "0s" : (fmt.duration(whole * 1000) ?? "—");
}
